<template>
    <div id="reports-search-container">
        <div class="saved-reports-filter">
            <ds-input-field
                id="search-input"
                v-model="search"
                label="Search reports"
                data-qa="search-results"
            >
                <template #leading>
                    <ds-icon name="search" />
                </template>
            </ds-input-field>

            <div class="filter-reports">
                <reports-filter
                    :all-reports="reportsList"
                    :report-type="reportTitle"
                    :unique-category-values="selectedCategoryList"
                    :is-centralized-reporting-hub-feature="isCentralizedReportingHubFeature"
                    @allFilteredReports="filteredReports"
                    @categoryLength="categoryLength"
                />
            </div>
        </div>

        <div v-if="!noReportResults">
            <ds-table-grid
                v-if="!noSearchResults"
                :data="reports"
                data-qa="all-reports"
                :class="{ 'few-reports': fewRecords }"
                :click-row="(row,event) => openReport(row, event)"
                :sort-field="sortField"
                :sort-ascending="sortAscending"
                @sort="sortTable"
            >
                <ds-table-grid-column
                    label="Report name"
                    prop="name"
                    width="180"
                >
                    <template slot-scope="props">
                        <!-- eslint-disable vue/no-v-html -->
                        <span class="report-name" v-html="props.name" />
                        <!-- eslint-enable vue/no-v-html -->
                    </template>
                </ds-table-grid-column>

                <ds-table-grid-column
                    label="Description"
                    prop="description"
                    width="350"
                />

                <ds-table-grid-column
                    label="Category"
                    prop="reportCategoryType"
                    width="150"
                    sortable
                />

                <ds-table-grid-column
                    width="60"
                    align="center"
                >
                    <template slot-scope="item">
                        <ds-dropdown
                            class="dropdown"
                            @click.stop
                        >
                            <ds-icon-button class="icon-only">
                                <ds-icon name="more-vertical" />
                            </ds-icon-button>

                            <div slot="menu">
                                <ul class="dropdown-menu-list pin-to-top">
                                    <li
                                        class="dropdown-menu-item"
                                        @click="pinToTop(item.name)"
                                    >
                                        <img src="/resources/funnel/images/svg/pin-icon.svg" alt="pin-image" />
                                        <div>Pin to top</div>
                                    </li>
                                    <li
                                        class="dropdown-menu-item"
                                        @click="viewReport(item)"
                                    >
                                        <ds-icon name="external-link" />
                                        <div>Open report</div>
                                    </li>
                                </ul>
                            </div>
                        </ds-dropdown>
                    </template>
                </ds-table-grid-column>
            </ds-table-grid>

            <div v-else data-qa="no-search-found">
                <h4>No results found for {{ search }}</h4>
            </div>
        </div>

        <div v-else data-qa="no-reports-found">
            <h4>No reports found</h4>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import ReportsFilter from './ReportsFilter.vue';

export default {
    components: {
        ReportsFilter,
    },

    props: {
        reportTitle: String,
        isCentralizedReportingHubFeature: Boolean,
    },

    data() {
        return {
            allReports: [],
            sortField: 'reportCategoryType',
            sortAscending: true,
            search: '',
            categoryItems: [],
            allReportsList: [],
            reportsList: [],
            allReportsCategory: [],
            categoryListLength: 1,
            reportsDataList: [],
        };
    },

    created() {
        this.load();
    },

    computed: {
        reports() {
            const search = this.search?.trim()?.toLowerCase();

            let allSearchedReports = this.allReports;

            if (this.categoryItems.length) {
                allSearchedReports = this.allReportsList;
            }

            return allSearchedReports?.filter(({ name, description }) => {
                return name?.toLowerCase().includes(search) || description?.toLowerCase().includes(search);
            });
        },

        fewRecords() {
            return this.reports?.length < 5;
        },

        noSearchResults() {
            return this.search && !this.reports?.length;
        },

        noReportResults() {
            return !this.allReports.length;
        },

        selectedCategoryList() {
             this.allReports?.forEach((report) => {
                 this.allReportsCategory.push(report.reportCategoryType);
             });

             let FilteredCategoryValues = [];

             if (this.categoryListLength) {
                 FilteredCategoryValues = this.allReportsCategory;
             }

             return FilteredCategoryValues?.filter((category, index) => {
                 return this.allReportsCategory.indexOf(category) === index;
             });
        },
    },

    methods: {
        async load() {
            this.loadAllReports();
        },

        async loadAllReports() {
            try {
                const response = await axios.get('/app/report/reports');

                this.reportsDataList = response.data;

                if (this.isCentralizedReportingHubFeature) {
                    this.reportsList = this.reportsDataList.filter((res) => res.reportCategoryType !== 'Sales' && res.reportCategoryType !== 'Visitors' && res.reportCategoryType !== 'Affiliate' && res.name !== 'Scheduled Actions' && res.name !== 'Follow-Up Sequence Dashboard Report' && res.name !== 'Email Dashboard Report');
                } else {
                    this.reportsList = this.reportsDataList;
                }
                this.selectedFilteredReports();
            } catch (e) {
                this.$error({ message: 'Unable get all reports' });
            }
        },

        selectedFilteredReports() {
            if (this.categoryListLength && this.categoryItems.length <= 0) {
                switch (this.reportTitle) {
                case 'Admin Reports':
                    this.allReports = this.reportsList.filter((item) => item.reportCategoryType === 'Administrative' || item.reportCategoryType === 'Email');
                    break;
                case 'CRM Reports':
                    this.allReports = this.reportsList.filter((item) => item.reportCategoryType === 'Sales' || item.reportCategoryType === 'Affiliate');
                    break;
                case 'Marketing Reports':
                    this.allReports = this.reportsList.filter((item) => item.reportCategoryType === 'Marketing');
                    break;
                case 'E-Commerce Reports':
                    this.allReports = this.reportsList.filter((item) => item.reportCategoryType === 'Order');
                    break;
                default:
                    this.allReports = this.reportsList;
                    break;
                }
            } else {
                this.allReports = this.reportsList;
            }
        },

        sortTable({ ascending }) {
            const factor = ascending === true ? -1 : 1;

            this.sortAscending = ascending;
            this.reports = this.reports.sort((prev, next) => {
                if (prev.reportCategoryType > next.reportCategoryType) return -1 * factor;
                if (prev.reportCategoryType < next.reportCategoryType) return 1 * factor;

                return 0;
            });
        },

        pinToTop(Request) {
            this.$emit('pinToTop', Request);
        },

        filteredReports(categoryItems, filteredReportsList) {
            this.allReportsList = filteredReportsList;
            this.categoryItems = categoryItems;
            this.sortAscending = true;
            this.loadAllReports();
        },

        openReport(report, event) {
            if (['td', 'span'].includes(event?.target?.localName)
              || ['numeric-cell', 'options-cell', 'cell'].includes(event?.target?.className)
            ) {
                this.$emit('openReport', report.uri);
            }
        },

        viewReport(item) {
            this.$emit('openReport', item.uri);
        },

        categoryLength(length) {
            this.categoryListLength = length;
        },
    },
};
</script>

<style lang="scss">
#reports-search-container {
    padding-top: $spacing-250;
    .few-reports .table-wrapper {
       height: calc(100vh - 300px);
       overflow-y: auto;
    }

    .saved-reports-filter {
        display: flex;
        justify-content: space-between;
    }

    .icon-text {
        padding-right: $spacing-200;
    }

    .pin-to-top div {
        margin-left: $spacing-200;
    }

    .pin-to-top {
        margin-top: 0;
        margin-bottom: 0;
    }

    .table-wrapper tr.selectable-row:last-child .dropdown-menu.open {
       inset: auto 0 0 auto !important;
       transform: translate(0, 26%) !important;
    }

    .selectable-row:hover {
       .report-name {
          text-decoration: underline;
       }
    }
}

</style>
